import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  VideoCamera, Plus, X, ArrowRight, CalendarCheck, Clock, Trash, CopySimple, Link as LinkIcon, ArrowSquareOut, ShieldCheck, Gear, Pulse,
} from "@phosphor-icons/react";
import { apiClient, formatApiErrorDetail, BACKEND_URL } from "../lib/api";

const DAYS = [
  { code: "mon", label: "Mon" },
  { code: "tue", label: "Tue" },
  { code: "wed", label: "Wed" },
  { code: "thu", label: "Thu" },
  { code: "fri", label: "Fri" },
  { code: "sat", label: "Sat" },
  { code: "sun", label: "Sun" },
];

const empty = {
  title: "",
  description: "",
  agenda: "",
  scheduled_at: "",
  duration_minutes: 30,
  host_name: "",
  host_email: "",
  attendees: [{ name: "", email: "", role: "guest" }],
  send_invites: true,
  timezone_label: "America/Chicago",
};

export default function MeetingsPanel() {
  const [tab, setTab] = useState("upcoming");
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(empty);
  const [showAvail, setShowAvail] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/meetings?filter=${tab}`);
      setMeetings(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const create = async () => {
    if (!form.title || !form.scheduled_at || !form.host_email) {
      toast.error("Title, time, and host email are required");
      return;
    }
    try {
      const payload = {
        ...form,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        attendees: form.attendees.filter((a) => a.email && a.name),
      };
      await apiClient.post("/meetings", payload);
      toast.success("Meeting scheduled. Invites sent.");
      setShowNew(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create meeting");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this meeting?")) return;
    try { await apiClient.delete(`/meetings/${id}`); toast.success("Cancelled"); load(); }
    catch (_e) { toast.error("Failed"); }
  };

  const copyLink = (m) => {
    const url = `${window.location.origin}/meet/${m.room_id}`;
    navigator.clipboard.writeText(url);
    toast.success("Meeting link copied");
  };

  const fmtWhen = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="space-y-4" data-testid="meetings-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-xl font-bold text-ink inline-flex items-center gap-2">
          <VideoCamera size={20} weight="fill" className="text-brandblue" />
          MedexOVA Meet · scheduling
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-600 inline-flex items-center gap-1">
            <Pulse size={11} weight="fill" /> live
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAvail(true)} data-testid="meetings-availability-btn" className="text-xs font-semibold border border-slate-200 hover:border-brandblue rounded-md px-3 py-2 inline-flex items-center gap-1.5">
            <Gear size={12} /> Availability
          </button>
          <button onClick={() => setShowNew(true)} data-testid="meetings-new-btn" className="text-xs font-semibold bg-brandblue hover:bg-brandblue-hover text-white rounded-md px-3 py-2 inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold" /> Schedule meeting
          </button>
        </div>
      </div>

      <div className="flex gap-1">
        {["upcoming", "today", "past", "all"].map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`meetings-tab-${k}`}
            className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full border transition ${
              tab === k ? "bg-brandblue text-white border-brandblue" : "bg-white text-slate-600 border-slate-200 hover:border-brandblue/50"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : meetings.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
          No meetings.
          <div className="mt-3">
            <button onClick={() => setShowNew(true)} className="text-xs font-semibold bg-brandblue hover:bg-brandblue-hover text-white rounded-md px-3 py-2 inline-flex items-center gap-1.5">
              <Plus size={12} /> Schedule one
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {meetings.map((m) => {
            const past = new Date(m.scheduled_at) < new Date();
            const cancelled = m.status === "cancelled";
            return (
              <div
                key={m.id}
                className={`rounded-xl border p-4 bg-white ${cancelled ? "border-red-200 bg-red-50/30 opacity-70" : past ? "border-slate-200" : "border-brandblue/30 shadow-sm"}`}
                data-testid={`meeting-card-${m.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brandblue inline-flex items-center gap-1.5">
                      <CalendarCheck size={11} /> {fmtWhen(m.scheduled_at)} · {m.duration_minutes} min
                    </div>
                    <div className="mt-1 font-heading text-lg font-bold text-ink truncate max-w-xs">{m.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Host: {m.host_name}</div>
                    {(m.attendees || []).length > 0 && (
                      <div className="text-[11px] text-slate-500 mt-1">
                        With: {m.attendees.map(a => a.name).join(", ")}
                      </div>
                    )}
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${
                    cancelled ? "bg-red-100 text-red-700" :
                    m.status === "in_progress" ? "bg-emerald-100 text-emerald-700" :
                    past ? "bg-slate-100 text-slate-500" : "bg-violet-100 text-violet-700"
                  }`}>
                    {cancelled ? "cancelled" : past ? "ended" : m.status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <a
                    href={`/meet/${m.room_id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`meeting-join-${m.id}`}
                    className="text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 bg-gradient-to-r from-brandblue to-ova text-white rounded-md px-3 py-1.5 hover:from-brandblue-deep hover:to-ova-hover"
                  >
                    <VideoCamera size={11} weight="fill" /> Join
                  </a>
                  <button
                    onClick={() => copyLink(m)}
                    data-testid={`meeting-copy-${m.id}`}
                    className="text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 border border-slate-200 hover:border-brandblue rounded-md px-3 py-1.5 text-slate-700"
                  >
                    <CopySimple size={11} /> Copy link
                  </button>
                  <a
                    href={`${BACKEND_URL}/api/meetings/${m.id}/ics`}
                    className="text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 border border-slate-200 hover:border-brandblue rounded-md px-3 py-1.5 text-slate-700"
                  >
                    .ics
                  </a>
                  {!cancelled && !past && (
                    <button onClick={() => cancel(m.id)} className="text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 text-red-600 hover:bg-red-50 rounded-md px-3 py-1.5 ml-auto">
                      <Trash size={11} /> Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Meeting Modal */}
      <AnimatePresence>
        {showNew && (
          <Modal onClose={() => setShowNew(false)} title="Schedule a meeting">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="m-title" />
              <F label="Date & time" type="datetime-local" value={form.scheduled_at} onChange={(v) => setForm({ ...form, scheduled_at: v })} testid="m-when" />
              <F label="Duration (min)" type="number" value={form.duration_minutes} onChange={(v) => setForm({ ...form, duration_minutes: parseInt(v, 10) || 30 })} />
              <F label="Time zone" value={form.timezone_label} onChange={(v) => setForm({ ...form, timezone_label: v })} />
              <F label="Host name" value={form.host_name} onChange={(v) => setForm({ ...form, host_name: v })} testid="m-host" />
              <F label="Host email" type="email" value={form.host_email} onChange={(v) => setForm({ ...form, host_email: v })} />
            </div>
            <div className="mt-4">
              <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">Description</label>
              <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full text-sm rounded-md border border-slate-200 px-3 py-2" />
            </div>
            <div className="mt-3">
              <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">Agenda</label>
              <textarea rows={3} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} className="mt-1 w-full text-sm rounded-md border border-slate-200 px-3 py-2"
                placeholder={"Intro\nMedexOVA capabilities\nQ&A\nNext steps"} />
            </div>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1">Guests</div>
              {form.attendees.map((a, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <input value={a.name} onChange={(e) => {
                    const next = [...form.attendees]; next[i] = { ...next[i], name: e.target.value }; setForm({ ...form, attendees: next });
                  }} placeholder="Name" className="flex-1 text-sm rounded-md border border-slate-200 px-3 py-2" />
                  <input value={a.email} onChange={(e) => {
                    const next = [...form.attendees]; next[i] = { ...next[i], email: e.target.value }; setForm({ ...form, attendees: next });
                  }} placeholder="email@example.com" className="flex-1 text-sm rounded-md border border-slate-200 px-3 py-2" />
                  <button onClick={() => {
                    const next = form.attendees.filter((_, j) => j !== i);
                    setForm({ ...form, attendees: next.length ? next : [{ name: "", email: "", role: "guest" }] });
                  }} className="h-9 w-9 grid place-items-center text-slate-400 hover:text-red-500"><X size={12} /></button>
                </div>
              ))}
              <button onClick={() => setForm({ ...form, attendees: [...form.attendees, { name: "", email: "", role: "guest" }] })}
                className="text-[11px] uppercase tracking-wider font-bold text-brandblue hover:text-brandblue-hover inline-flex items-center gap-1">
                <Plus size={11} /> Add guest
              </button>
            </div>
            <label className="mt-4 flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={form.send_invites} onChange={(e) => setForm({ ...form, send_invites: e.target.checked })} />
              Send email invites with .ics calendar attachment
            </label>
            <div className="mt-5 flex justify-between">
              <button onClick={() => setShowNew(false)} className="text-sm text-slate-600 hover:text-ink">Cancel</button>
              <button onClick={create} data-testid="m-submit" className="bg-brandblue hover:bg-brandblue-hover text-white font-semibold rounded-md px-5 py-2 text-sm inline-flex items-center gap-2">
                <CalendarCheck size={14} weight="fill" /> Schedule + send invites
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Availability Modal */}
      <AnimatePresence>
        {showAvail && <AvailabilityEditor onClose={() => setShowAvail(false)} />}
      </AnimatePresence>
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <motion.div initial={{ y: 24, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 24, scale: 0.98 }}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="meetings-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-heading text-lg font-bold text-ink">{title}</div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded hover:bg-slate-100"><X size={14} /></button>
        </div>
        <div className="p-6">{children}</div>
      </motion.div>
    </motion.div>
  );
}

function F({ label, children, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</label>
      {children ?? (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="mt-1 w-full text-sm rounded-md border border-slate-200 px-3 py-2" />
      )}
    </div>
  );
}

function AvailabilityEditor({ onClose }) {
  const [cfg, setCfg] = useState({
    host_email: "",
    host_name: "",
    host_slug: "shelton",
    slot_minutes: 30,
    buffer_minutes: 0,
    timezone_label: "America/Chicago",
    rules: ["mon", "tue", "wed", "thu", "fri"].map((w) => ({ weekday: w, start_hhmm: "09:00", end_hhmm: "17:00" })),
  });

  useEffect(() => {
    apiClient.get(`/availability/${cfg.host_slug}`).then(({ data }) => {
      setCfg({ ...cfg, ...data, rules: data.rules?.length ? data.rules : cfg.rules });
    }).catch(() => { /* first-time setup */ });
    // eslint-disable-next-line
  }, []);

  const toggleDay = (code) => {
    const exists = cfg.rules.find((r) => r.weekday === code);
    if (exists) setCfg({ ...cfg, rules: cfg.rules.filter((r) => r.weekday !== code) });
    else setCfg({ ...cfg, rules: [...cfg.rules, { weekday: code, start_hhmm: "09:00", end_hhmm: "17:00" }] });
  };

  const save = async () => {
    if (!cfg.host_email || !cfg.host_name || !cfg.host_slug) {
      toast.error("Host name, email, and slug are required");
      return;
    }
    try {
      await apiClient.put("/availability", cfg);
      toast.success("Availability saved");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const publicUrl = `${window.location.origin}/schedule/${cfg.host_slug}`;
  const copy = () => { navigator.clipboard.writeText(publicUrl); toast.success("Public schedule URL copied"); };

  return (
    <Modal onClose={onClose} title="Public scheduling availability">
      <p className="text-xs text-slate-500 mb-4">
        Anyone with the link below can book a {cfg.slot_minutes}-minute call with you in the windows you set here.
        We auto-block times you already have meetings in.
      </p>
      <div className="rounded-lg border border-brandblue/30 bg-brandblue/5 p-3 mb-4 flex items-center justify-between gap-2">
        <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-brandblue hover:underline truncate">{publicUrl}</a>
        <button onClick={copy} className="text-[10px] uppercase tracking-wider font-bold text-brandblue inline-flex items-center gap-1">
          <CopySimple size={11} /> Copy
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Host name" value={cfg.host_name} onChange={(v) => setCfg({ ...cfg, host_name: v })} testid="av-host-name" />
        <F label="Host email" type="email" value={cfg.host_email} onChange={(v) => setCfg({ ...cfg, host_email: v })} testid="av-host-email" />
        <F label="URL slug" value={cfg.host_slug} onChange={(v) => setCfg({ ...cfg, host_slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "") })} testid="av-slug" />
        <F label="Time zone" value={cfg.timezone_label} onChange={(v) => setCfg({ ...cfg, timezone_label: v })} />
        <F label="Slot length (min)" type="number" value={cfg.slot_minutes} onChange={(v) => setCfg({ ...cfg, slot_minutes: parseInt(v, 10) || 30 })} />
        <F label="Buffer between (min)" type="number" value={cfg.buffer_minutes} onChange={(v) => setCfg({ ...cfg, buffer_minutes: parseInt(v, 10) || 0 })} />
      </div>
      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Weekly windows</div>
        <div className="space-y-1.5">
          {DAYS.map((d) => {
            const rule = cfg.rules.find((r) => r.weekday === d.code);
            const enabled = !!rule;
            return (
              <div key={d.code} className="flex items-center gap-2">
                <button onClick={() => toggleDay(d.code)} className={`w-14 h-9 rounded-md text-xs font-bold ${enabled ? "bg-brandblue text-white" : "bg-slate-100 text-slate-500"}`}>
                  {d.label}
                </button>
                {enabled ? (
                  <>
                    <input type="time" value={rule.start_hhmm} onChange={(e) => {
                      const next = cfg.rules.map((r) => r.weekday === d.code ? { ...r, start_hhmm: e.target.value } : r);
                      setCfg({ ...cfg, rules: next });
                    }} className="text-sm rounded-md border border-slate-200 px-2 py-1.5" />
                    <span className="text-xs text-slate-400">to</span>
                    <input type="time" value={rule.end_hhmm} onChange={(e) => {
                      const next = cfg.rules.map((r) => r.weekday === d.code ? { ...r, end_hhmm: e.target.value } : r);
                      setCfg({ ...cfg, rules: next });
                    }} className="text-sm rounded-md border border-slate-200 px-2 py-1.5" />
                  </>
                ) : (
                  <span className="text-xs text-slate-400">Off</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-6 flex justify-between">
        <button onClick={onClose} className="text-sm text-slate-600 hover:text-ink">Cancel</button>
        <button onClick={save} data-testid="av-save" className="bg-brandblue hover:bg-brandblue-hover text-white font-semibold rounded-md px-5 py-2 text-sm inline-flex items-center gap-2">
          <ShieldCheck size={14} weight="fill" /> Save availability
        </button>
      </div>
    </Modal>
  );
}
